import React from "react";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import {
	SliderCore,
	SliderModules,
	Slider,
	Slide,
} from "@insightfulscience/atomic-react/r2/Slider";

import {
	ColLogo,
	RowLogo,
	SectionBlockquotesSlider,
	SectionLogos,
	BlockQuoteSlide,
} from "@insightfulscience/atomic-react/r2/CustomersPartials";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import Html from "@insightfulscience/smart-react/Html";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import SectionTryForFree from "@src/SectionTryForFree";
import SiteLayout from "@src/SiteLayout";
import PreloadImages from "@src/PreloadImages";
import resources from "../locales/customers-page";

SliderCore.use([
	SliderModules.Navigation,
	SliderModules.Pagination,
	SliderModules.A11y,
	SliderModules.Autoplay,
]);

const sliderConfig = {
	observer: true,
	autoHeight: true,
	slidesPerView: 1,
	autoplay: {
		delay: 5000,
		disableOnInteraction: false,
	},
	pagination: {
		clickable: true,
	},
};
const assetsPath = "/images/snapgene/customers";
const alternativeAssets = "/images/common/companies";

const About = () => {
	const [t] = useLocale("customers");
	return (
		<SiteLayout hero>
			<PageMeta title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					locale={t("META_LOCALE")}
					type={t("META_TYPE")}
					url={t("META_URL")}
					image={t("META_IMAGE")}
					imageSecureUrl={t("META_IMAGE_SECURE")}
					siteName={t("META_SITE_NAME")}
					articlePublisher={t("META_ARTICLE_PUBLISHER")}
				/>
				<PageMeta.Twitter
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					site={t("META_TWITTER_SITE")}
					card={t("META_TWITTER_CARD")}
					image={t("META_TWITTER_IMAGE")}
					creator={t("META_TWITTER_CREATOR")}
				/>
				<PageMeta.Canonical href={t("META_CANONICAL")} />
				<PageMeta.Author content={t("META_AUTHOR")} />
				<PreloadImages
					imagesHref={[
						"/images/snapgene/customers/stanford-university.png",
						"/images/snapgene/customers/sanofi.png",
						"/images/snapgene/customers/novartis.png",
						"/images/snapgene/customers/mit.png",
						"/images/snapgene/customers/genewiz.png",
						"/images/snapgene/customers/genscript.png",
						"/images/snapgene/customers/kite.png",
						"/images/snapgene/customers/biomarin.png",
					]}
				/>
			</PageMeta>
			<Hero>
				<HeroTitle>{t("PAGE_HERO_TITLE")}</HeroTitle>
				<HeroDescription>{t("PAGE_HERO_DESCRIPTION")}</HeroDescription>
			</Hero>
			<SectionLogos>
				<RowLogo>
					<ColLogo src={`${assetsPath}/stanford-university.png`} />
					<ColLogo src={`${assetsPath}/sanofi.png`} />
					<ColLogo src={`${assetsPath}/novartis.png`} />
					<ColLogo src={`${assetsPath}/mit.png`} />
				</RowLogo>
				<RowLogo>
					<ColLogo src={`${assetsPath}/genewiz.png`} />
					<ColLogo src={`${assetsPath}/genscript.png`} />
					<ColLogo src={`${assetsPath}/kite.png`} />
					<ColLogo src={`${assetsPath}/biomarin.png`} />
				</RowLogo>
			</SectionLogos>

			<SectionBlockquotesSlider>
				<Slider {...sliderConfig}>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_AUTHOR_1")}
							institution={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_INSTITUTION_1")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_DESCRIPTION_1")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_AUTHOR_2")}
							institution={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_INSTITUTION_2")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_DESCRIPTION_2")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_AUTHOR_3")}
							institution={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_INSTITUTION_3")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_DESCRIPTION_3")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_AUTHOR_4")}
							institution={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_INSTITUTION_4")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_DESCRIPTION_4")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_AUTHOR_5")}
							institution={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_INSTITUTION_5")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_DESCRIPTION_5")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_AUTHOR_6")}
							institution={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_INSTITUTION_6")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_DESCRIPTION_6")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_AUTHOR_7")}
							institution={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_INSTITUTION_7")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_DESCRIPTION_7")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_AUTHOR_8")}
							institution={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_INSTITUTION_8")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_DESCRIPTION_8")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_AUTHOR_9")}
							institution={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_INSTITUTION_9")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_DESCRIPTION_9")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_AUTHOR_10")}
							institution={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_INSTITUTION_10")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_DESCRIPTION_10")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_AUTHOR_11")}
							institution={t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_INSTITUTION_11")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_FIRST_BLOCK_DESCRIPTION_11")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
				</Slider>
			</SectionBlockquotesSlider>

			<SectionLogos>
				<RowLogo>
					<ColLogo src={`${assetsPath}/abcam.png`} />
					<ColLogo src={`${assetsPath}/emory.png`} />
					<ColLogo src={`${assetsPath}/ucla.png`} />
					<ColLogo src={`${assetsPath}/uottawa.png`} />
				</RowLogo>
				<RowLogo>
					<ColLogo src={`${assetsPath}/copenhagen-university.png`} />
					<ColLogo src={`${assetsPath}/rutgers.png`} />
					<ColLogo src={`${assetsPath}/genentech.png`} />
					<ColLogo src={`${assetsPath}/glasgow-university.png`} />
				</RowLogo>
			</SectionLogos>

			<SectionBlockquotesSlider>
				<Slider {...sliderConfig}>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_AUTHOR_1")}
							institution={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_INSTITUTION_1")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_DESCRIPTION_1")}
							</Html>
						</BlockQuoteSlide>{" "}
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_AUTHOR_2")}
							institution={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_INSTITUTION_2")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_DESCRIPTION_2")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_AUTHOR_3")}
							institution={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_INSTITUTION_3")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_DESCRIPTION_3")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_AUTHOR_4")}
							institution={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_INSTITUTION_4")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_DESCRIPTION_4")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_AUTHOR_5")}
							institution={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_INSTITUTION_5")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_DESCRIPTION_5")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_AUTHOR_6")}
							institution={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_INSTITUTION_6")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_DESCRIPTION_6")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_AUTHOR_7")}
							institution={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_INSTITUTION_7")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_DESCRIPTION_7")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_AUTHOR_8")}
							institution={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_INSTITUTION_8")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_DESCRIPTION_8")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_AUTHOR_9")}
							institution={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_INSTITUTION_9")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_DESCRIPTION_9")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_AUTHOR_10")}
							institution={t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_INSTITUTION_10")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_SECOND_BLOCK_DESCRIPTION_10")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
				</Slider>
			</SectionBlockquotesSlider>

			<SectionLogos>
				<RowLogo>
					<ColLogo src={`${assetsPath}/novozymes.png`} />
					<ColLogo src={`${assetsPath}/vib.png`} />
					<ColLogo src={`${assetsPath}/cambridge-university.png`} />
					<ColLogo src={`${alternativeAssets}/regeneron-logo.png`} />
				</RowLogo>
				<RowLogo>
					<ColLogo src={`${assetsPath}/oxford-university.png`} />
					<ColLogo src={`${assetsPath}/precision-biosciences.png`} />
					<ColLogo src={`${assetsPath}/akademie-ved-cr.png`} />
					<ColLogo src={`${assetsPath}/csiro.png`} />
				</RowLogo>
			</SectionLogos>

			<SectionBlockquotesSlider>
				<Slider {...sliderConfig}>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_AUTHOR_1")}
							institution={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_INSTITUTION_1")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_DESCRIPTION_1")}
							</Html>
						</BlockQuoteSlide>{" "}
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_AUTHOR_2")}
							institution={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_INSTITUTION_2")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_DESCRIPTION_2")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_AUTHOR_3")}
							institution={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_INSTITUTION_3")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_DESCRIPTION_3")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_AUTHOR_4")}
							institution={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_INSTITUTION_4")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_DESCRIPTION_4")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_AUTHOR_5")}
							institution={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_INSTITUTION_5")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_DESCRIPTION_5")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_AUTHOR_6")}
							institution={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_INSTITUTION_6")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_DESCRIPTION_6")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_AUTHOR_7")}
							institution={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_INSTITUTION_7")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_DESCRIPTION_7")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_AUTHOR_8")}
							institution={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_INSTITUTION_8")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_DESCRIPTION_8")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_AUTHOR_9")}
							institution={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_INSTITUTION_9")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_DESCRIPTION_9")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
					<Slide>
						<BlockQuoteSlide
							author={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_AUTHOR_10")}
							institution={t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_INSTITUTION_10")}
						>
							<Html Tag="span">
								{t("PAGE_BLOCK_QUOTE_THIRD_BLOCK_DESCRIPTION_10")}
							</Html>
						</BlockQuoteSlide>
					</Slide>
				</Slider>
			</SectionBlockquotesSlider>

			<SectionLogos>
				<RowLogo>
					<ColLogo src={`${assetsPath}/garvan-institute.png`} />
					<ColLogo src={`${assetsPath}/twincore.png`} />
					<ColLogo src={`${assetsPath}/sydney-university.png`} />
					<ColLogo src={`${assetsPath}/paul-ehrlich-institut.png`} />
				</RowLogo>
				<RowLogo>
					<ColLogo src={`${assetsPath}/dundee-university.png`} />
					<ColLogo src={`${assetsPath}/sinica.png`} />
					<ColLogo src={`${assetsPath}/newhaven-university.png`} />
					<ColLogo src={`${assetsPath}/seattle-genetics.png`} />
				</RowLogo>
			</SectionLogos>
			<SectionTryForFree />
		</SiteLayout>
	);
};

export default pipe(About, WithLocale({ resources }));
